if (!customElements.get('oypt-bestseller-slider')) {
  class OyptBestsellerSlider extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
      this.viewAllLinks = Array.from(this.querySelectorAll('[data-view-all]'));
      this.controls = this.querySelector('[data-controls]');
      this.progressWrap = this.querySelector('[data-progress-wrap]');
      this.progress = this.querySelector('[data-progress]');
      this.previousButton = this.querySelector('[data-previous]');
      this.nextButton = this.querySelector('[data-next]');
      this.sticky = this.querySelector('[data-sticky]');
      this.sectionHeader = this.querySelector('.oypt-bestseller__header');
      this.header = document.querySelector('sticky-header');
      this.mobileDock = document.querySelector('mobile-dock');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;
      this.scrollLinked = false;
      this.pinOffset = 0;
      this.maxScroll = 0;
      this.syncedPosition = 0;
      this.drag = null;
      this.clickBlockedUntil = 0;

      if (!this.panels.length) return;

      const options = { signal: this.controller.signal };
      const tabList = this.querySelector('[data-tabs]');

      if (tabList) {
        tabList.setAttribute('role', 'tablist');
        this.tabs.forEach((tab, index) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-controls', this.panels[index].id);
          this.panels[index].setAttribute('role', 'tabpanel');
          this.panels[index].setAttribute('aria-labelledby', tab.id);
          tab.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectPanel(index);
          }, options);
          tab.addEventListener('keydown', (event) => {
            this.onTabKeydown(event, index);
          }, options);
        });
      }

      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-track]');
        track.addEventListener('scroll', () => this.onTrackScroll(), { ...options, passive: true });
        track.addEventListener('keydown', (event) => this.onTrackKeydown(event), options);
        track.addEventListener('pointerdown', (event) => this.onPointerDown(event, track), options);
        track.addEventListener('dragstart', (event) => event.preventDefault(), options);
        track.addEventListener('click', (event) => {
          if (performance.now() >= this.clickBlockedUntil) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, { ...options, capture: true });
      });

      this.previousButton.addEventListener('click', () => this.move(-1), options);
      this.nextButton.addEventListener('click', () => this.move(1), options);
      document.addEventListener('pointermove', (event) => this.onPointerMove(event), options);
      document.addEventListener('pointerup', (event) => this.onPointerUp(event), options);
      document.addEventListener('pointercancel', (event) => this.onPointerUp(event), options);
      window.addEventListener('scroll', () => this.scheduleScroll(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleMeasure(), { ...options, passive: true });
      this.reducedMotion.addEventListener('change', () => this.scheduleMeasure(), options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.panels.findIndex((panel) => panel === event.target || panel.contains(event.target));
        if (index >= 0) this.selectPanel(index);
      }, options);

      this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
      this.resizeObserver.observe(this.sticky);
      if (this.progressWrap) this.resizeObserver.observe(this.progressWrap);
      if (this.header) this.resizeObserver.observe(this.header);
      if (this.mobileDock) this.resizeObserver.observe(this.mobileDock);
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.scheduleMeasure();
      });
      this.selectPanel(0);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.drag) this.drag.track.classList.remove('is-dragging');
      this.drag = null;
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.measureFrame);
      this.classList.remove('is-scroll-linked', 'is-scroll-ready');
    }

    get track() {
      return this.panels[this.activeIndex].querySelector('[data-track]');
    }

    selectPanel(index) {
      this.activeIndex = index;
      this.panels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
      });
      this.viewAllLinks.forEach((link) => {
        link.hidden = link.dataset.viewAll !== this.panels[index].id;
      });
      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.scheduleUpdate();
      this.scheduleMeasure();
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateControls());
    }

    updateControls() {
      const track = this.track;
      const maxScroll = track.scrollWidth - track.clientWidth;
      const position = Math.abs(track.scrollLeft);
      this.controls.hidden = maxScroll < 2;
      if (this.progressWrap) this.progressWrap.hidden = maxScroll < 2;
      if (this.progress) {
        this.progress.value = maxScroll > 0 ? Math.min(100, (position / maxScroll) * 100) : 0;
      }
      this.previousButton.disabled = position < 2;
      this.nextButton.disabled = position >= maxScroll - 2;
      this.previousButton.setAttribute('aria-controls', this.panels[this.activeIndex].id);
      this.nextButton.setAttribute('aria-controls', this.panels[this.activeIndex].id);
    }

    scheduleMeasure() {
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = requestAnimationFrame(() => this.measureScroll());
    }

    measureScroll() {
      const eligible = this.dataset.scrollDriven === 'true'
        && this.dataset.designMode !== 'true' && !this.reducedMotion.matches;
      const headerHeight = this.header?.getBoundingClientRect().height || 0;
      this.style.setProperty('--oypt-slider-header', `${headerHeight}px`);
      this.classList.toggle('is-scroll-ready', eligible);
      const sectionHeaderHeight = this.sectionHeader?.getBoundingClientRect().height || 0;
      this.style.setProperty('--oypt-slider-section-header', `${sectionHeaderHeight}px`);
      const mobileDockHeight = this.mobileDock?.getBoundingClientRect().height || 0;
      this.style.setProperty('--oypt-slider-dock', `${mobileDockHeight}px`);
      const progressHeight = this.progressWrap?.getBoundingClientRect().height || 0;
      this.style.setProperty('--oypt-slider-progress', `${progressHeight}px`);
      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const height = this.sticky.getBoundingClientRect().height;
      const viewportHeight = document.documentElement.clientHeight;
      const sectionHeaderMargin = this.sectionHeader
        ? parseFloat(getComputedStyle(this.sectionHeader).marginBottom) || 0 : 0;
      const productAreaHeight = height - sectionHeaderHeight - sectionHeaderMargin;
      this.scrollLinked = eligible && this.maxScroll > 2
        && Boolean(this.track.querySelector('.oypt-bestseller__item'))
        && productAreaHeight <= viewportHeight + 1;
      this.pinOffset = this.scrollLinked && height > viewportHeight
        ? sectionHeaderHeight + sectionHeaderMargin : 0;
      this.style.setProperty('--oypt-slider-pin-offset', `${this.pinOffset}px`);
      this.style.setProperty('--oypt-slider-distance', `${this.maxScroll}px`);
      this.style.setProperty('--oypt-slider-height', `${height}px`);
      this.classList.toggle('is-scroll-linked', this.scrollLinked);
      this.syncFromPage();
      this.updateControls();
    }

    scheduleScroll() {
      if (!this.scrollLinked) return;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.syncFromPage());
    }

    get scrollStart() {
      return this.getBoundingClientRect().top + window.scrollY + (this.pinOffset || 0);
    }

    syncFromPage() {
      if (!this.scrollLinked) return;
      const position = Math.min(this.maxScroll, Math.max(0, window.scrollY - this.scrollStart));
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.syncedPosition = position;
      this.track.scrollLeft = position * (rtl ? -1 : 1);
    }

    onTrackScroll() {
      this.scheduleUpdate();
      if (!this.scrollLinked) return;
      const pagePosition = window.scrollY - this.scrollStart;
      const trackPosition = Math.min(this.maxScroll, Math.abs(this.track.scrollLeft));
      if (pagePosition < -1 || pagePosition > this.maxScroll + 1) return;
      // Ignore the scroll event produced by our own page-to-track update.
      if (Math.abs(this.syncedPosition - trackPosition) < 2) return;
      this.syncedPosition = trackPosition;
      window.scrollTo({ top: this.scrollStart + trackPosition, behavior: 'instant' });
    }

    scrollToPosition(position) {
      const track = this.track;
      const rtl = getComputedStyle(track).direction === 'rtl';
      const clamped = Math.min(Math.max(0, track.scrollWidth - track.clientWidth), Math.max(0, position));
      if (this.scrollLinked) {
        this.syncedPosition = clamped;
        window.scrollTo({ top: this.scrollStart + clamped, behavior: 'instant' });
        track.scrollLeft = clamped * (rtl ? -1 : 1);
      } else {
        track.scrollTo({
          left: clamped * (rtl ? -1 : 1),
          behavior: this.reducedMotion.matches ? 'instant' : 'smooth'
        });
      }
    }

    onPointerDown(event, track) {
      if (event.button !== 0 || !event.isPrimary || !track.querySelector('.oypt-bestseller__item')) return;
      this.pointerId = event.pointerId;
      if (event.pointerType !== 'mouse') return;

      this.drag = {
        track,
        startX: event.clientX,
        startY: event.clientY,
        scrollLeft: track.scrollLeft,
        moved: false
      };
    }

    onPointerMove(event) {
      if (!this.drag || event.pointerId !== this.pointerId) return;
      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;

      if (!this.drag.moved) {
        if (Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX)) {
          this.drag = null;
          return;
        }
        if (Math.abs(deltaX) < 6) return;
        this.drag.moved = true;
        this.drag.track.classList.add('is-dragging');
        this.drag.track.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      const rtl = getComputedStyle(this.drag.track).direction === 'rtl';
      this.scrollToPosition((this.drag.scrollLeft - deltaX) * (rtl ? -1 : 1));
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      this.pointerId = null;

      if (this.drag) {
        const { track, moved } = this.drag;
        this.drag = null;
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);

        if (moved) {
          this.clickBlockedUntil = performance.now() + 350;
          track.classList.remove('is-dragging');
          const item = track.querySelector('.oypt-bestseller__item');
          if (item) {
            const itemWidth = item.getBoundingClientRect().width;
            const position = Math.round(Math.abs(track.scrollLeft) / itemWidth) * itemWidth;
            this.scrollToPosition(position);
          }
        }
      }
    }

    move(direction) {
      const track = this.track;
      const item = track.querySelector('.oypt-bestseller__item');
      if (!item) return;

      const itemWidth = item.getBoundingClientRect().width;
      const current = Math.round(Math.abs(track.scrollLeft) / itemWidth);
      this.scrollToPosition((current + direction) * itemWidth);
    }

    onTrackKeydown(event) {
      if (event.target !== this.track) return;
      const rtl = getComputedStyle(this.track).direction === 'rtl';

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.scrollToPosition(event.key === 'Home' ? 0 : this.track.scrollWidth);
      }
    }

    onTabKeydown(event, index) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      let nextIndex = index;

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const direction = (event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1);
        nextIndex = (index + direction + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else if (event.key === ' ') {
        event.preventDefault();
        this.selectPanel(index);
        return;
      } else {
        return;
      }

      event.preventDefault();
      this.selectPanel(nextIndex);
      this.tabs[nextIndex].focus();
    }
  }

  customElements.define('oypt-bestseller-slider', OyptBestsellerSlider);
}
